import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from './Modal';

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * Confirmacao de identidade antes de assinar (revisao, aprovacao, leitura).
 *
 * Emite um step-up de curta duracao no servidor; quem executa a acao em si e
 * quem chamou este componente, via `onConfirmed`. Assim a assinatura continua
 * sendo um ato explicito, e nao algo disparado por um redirect de volta.
 */
export default function SignatureModal({ title, description, onClose, onConfirmed }) {
  const [methods, setMethods] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/auth/step-up/methods')
      .then(({ data }) => setMethods(data))
      .catch(() => setError('Não foi possível verificar as formas de confirmação'));
  }, []);

  async function confirmWithPassword(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/step-up/password', { password });
      setPassword('');
      await onConfirmed();
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível confirmar');
      setBusy(false);
    }
  }

  function confirmWithMicrosoft() {
    // Sai da aplicacao e volta para esta mesma tela apos reautenticar
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/api/auth/step-up/azure?returnTo=${encodeURIComponent(returnTo)}`;
  }

  return (
    <Modal title={title} onClose={onClose}>
      <p className="text-sm text-ink-secondary mb-4">{description}</p>

      {error && (
        <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">
          {error}
        </div>
      )}

      {!methods && <div className="text-sm text-ink-muted">Carregando...</div>}

      {methods && (
        <>
          {methods.password && (
            <form onSubmit={confirmWithPassword}>
              <label className="block text-sm font-medium text-ink-secondary mb-1">Sua senha</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
                autoComplete="current-password"
              />
              <button type="submit" className="btn btn-primary w-full mt-3" disabled={busy || !password}>
                {busy ? 'Confirmando...' : 'Assinar com senha'}
              </button>
            </form>
          )}

          {methods.password && methods.microsoft && (
            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-current opacity-10" />
              <span className="text-xs text-ink-muted">ou</span>
              <div className="h-px flex-1 bg-current opacity-10" />
            </div>
          )}

          {methods.microsoft && (
            <button
              type="button"
              onClick={confirmWithMicrosoft}
              className="btn w-full flex items-center justify-center gap-2 border bg-surface-100 hover:bg-surface-base"
              disabled={busy}
            >
              <MicrosoftLogo />
              Confirmar com a Microsoft
            </button>
          )}

          {!methods.password && !methods.microsoft && (
            <div className="text-sm text-status-rejected">
              Nenhuma forma de confirmação disponível para esta conta. Procure um administrador.
            </div>
          )}

          <p className="text-xs text-ink-muted mt-4">
            A confirmacao vale por {Math.round((methods.valid_for_seconds || 300) / 60)} minutos.
            O metodo usado fica registrado na auditoria junto da sua assinatura.
          </p>
        </>
      )}
    </Modal>
  );
}
